"use client";

import React, { useState, useEffect, useCallback } from "react";
import { signIn, useSession } from "next-auth/react";
import {
  ActionableNotification,
  Button,
  Checkbox,
  Form,
  InlineLoading,
  InlineNotification,
  PasswordInput,
  TextInput,
  Theme,
} from "@carbon/react";
import { ArrowRight } from "@carbon/icons-react";
import { useSearchParams } from "next/navigation";
import { classifyCredentialSignInResult } from "@/lib/auth/credentialSignInResult";
import { isSafeRedirect } from "@/lib/utils/safeRedirect";


interface LoginFormProps {
  initialUsername?: string;
  initialRemember?: boolean;
  appVersion: string;
  /** Entra SSO is configured on this instance — render the SSO button. */
  azureAdEnabled?: boolean;
  /** Maximo username/password is usable here. False on SAML-only backends. */
  credentialsEnabled?: boolean;
}

export default function LoginForm({
  initialUsername = "",
  initialRemember = false,
  appVersion,
  azureAdEnabled = false,
  credentialsEnabled = true,
}: LoginFormProps) {
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMaximoDown, setIsMaximoDown] = useState<boolean | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const { status: sessionStatus } = useSession();

  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl");

  const checkSystemStatus = useCallback(async () => {
    setIsCheckingStatus(true);
    try {
      const res = await fetch("/api/system-check");
      const data = res.ok ? await res.json() : null;
      if (data && typeof data.isMaximoUp === "boolean") {
        setIsMaximoDown(!data.isMaximoUp);
      }
    } catch {
      setIsMaximoDown(null);
    } finally {
      setIsCheckingStatus(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/system-check")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data && typeof data.isMaximoUp === "boolean") {
          setIsMaximoDown(!data.isMaximoUp);
        }
      })
      .catch(() => {
        if (!cancelled) setIsMaximoDown(null);
      })
      .finally(() => {
        if (!cancelled) setIsCheckingStatus(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFormLogIn = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    // Read form data BEFORE any state update — disabling inputs strips them from FormData.
    const formData = new FormData(form);
    const rawUsername = formData.get("username");
    const username = typeof rawUsername === "string" ? rawUsername.trim() : "";
    const password = (formData.get("password") as string) ?? "";
    const rememberUsername = formData.get("rememberUsername") === "on";

    if (!username || username === "null" || username === "undefined") {
      setError("Please enter your username");
      return;
    }

    setIsLoggingIn(true);
    setError(null);

    try {
      const statusRes = await fetch("/api/system-check");
      if (statusRes.ok) {
        const status = await statusRes.json();
        if (status && status.isMaximoUp === false) {
          setError("Maximo is currently unavailable. Please try again later.");
          setIsLoggingIn(false);
          return;
        }
      }
    } catch {
      // Network error calling system-check — proceed; auth may still work.
    }

    const result = await signIn("credentials", {
      username,
      password,
      redirect: false,
    });
    const signInOutcome = classifyCredentialSignInResult(result);

    if (signInOutcome === "success") {
      // Always set Secure — on http://localhost the browser silently
      // ignores the cookie write, which is acceptable for remember-me
      // in dev (devs can sign in fresh each time).
      if (rememberUsername && username) {
        document.cookie = `rememberUsername=${encodeURIComponent(
          username,
        )}; path=/; max-age=31536000; Secure; SameSite=Lax`;
      } else {
        document.cookie = `rememberUsername=; path=/; max-age=0; Secure; SameSite=Lax`;
      }
      const destination = isSafeRedirect(callbackUrl) ? callbackUrl : "/home";

      // Brief delay lets NextAuth complete session refetch before unload.
      // Without it: ECONNRESET when the refetch is aborted by navigation.
      // Full reload ensures ServerProvider re-runs with fresh session/configs.
      await new Promise((resolve) => setTimeout(resolve, 150));
      window.location.href = destination;
    } else if (signInOutcome === "csrf_expired") {
      setError("Your sign-in request expired. Please try again.");
      setIsLoggingIn(false);
    } else {
      setError("Invalid username or password");
      setIsLoggingIn(false);
    }
  };

  // The initial useSession request may set a CSRF cookie. Do not allow a
  // credentials submission to start until that request has settled, otherwise
  // its late response can overwrite the token fetched by signIn().
  const disabled =
    isLoggingIn ||
    isMaximoDown === true ||
    isCheckingStatus ||
    sessionStatus === "loading";

  return (
    <Theme theme="g100">
      <div className="ml-login">
        <header className="ml-login__brand">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/branding/maxlogic-light.png"
            alt="MaxLogic"
            className="ml-login__logo"
            onClick={() =>
              window.open("https://www.maxlogicgroup.com/", "_blank")
            }
          />
        </header>

        <section className="ml-login__card-wrap">
          <div className="ml-login__card">
            <h1 className="ml-login__title">Sign in</h1>
            <p className="ml-login__subtitle">Welcome back to Data Portal</p>

            {isMaximoDown === true && (
              <ActionableNotification
                className="ml-login__error"
                kind="warning"
                title="Maximo is currently unavailable. Login is disabled. Please try again later."
                hideCloseButton
                actionButtonLabel="Check again"
                onActionButtonClick={checkSystemStatus}
                lowContrast
              />
            )}

            {error && (
              <InlineNotification
                className="ml-login__error"
                kind="error"
                title="Sign in failed"
                subtitle={error}
                lowContrast
                hideCloseButton
              />
            )}

            {azureAdEnabled && (
              <div className="ml-login__sso">
                <Button
                  kind={credentialsEnabled ? "tertiary" : "primary"}
                  type="button"
                  className="ml-login__sso-button"
                  disabled={isLoggingIn}
                  onClick={() =>
                    signIn("azure-ad", {
                      callbackUrl:
                        callbackUrl && isSafeRedirect(callbackUrl)
                          ? callbackUrl
                          : "/home",
                    })
                  }
                >
                  Sign in with Microsoft
                </Button>
                {credentialsEnabled && (
                  <p className="ml-login__sso-divider">or</p>
                )}
              </div>
            )}

            {credentialsEnabled && (
            <Form onSubmit={handleFormLogIn} className="ml-login__form" noValidate>
              <div className="ml-login__field">
                <TextInput
                  id="username"
                  name="username"
                  required
                  labelText="Username"
                  placeholder="Enter your username"
                  defaultValue={initialUsername}
                  autoComplete="username"
                  onChange={() => {
                    if (error) setError(null);
                  }}
                  disabled={disabled}
                />
              </div>
              <div className="ml-login__field">
                <PasswordInput
                  id="password"
                  name="password"
                  type="password"
                  required
                  labelText="Password"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  onChange={() => {
                    if (error) setError(null);
                  }}
                  disabled={disabled}
                />
              </div>

              <Button
                kind="primary"
                type="submit"
                className="ml-login__submit"
                disabled={disabled}
                renderIcon={isLoggingIn ? InlineLoading : ArrowRight}
              >
                {isLoggingIn ? "Signing in…" : "Continue"}
              </Button>

              <Checkbox
                id="remember-username-checkbox"
                name="rememberUsername"
                labelText="Remember username"
                defaultChecked={initialRemember}
                disabled={disabled}
                className="ml-login__remember"
              />
            </Form>
            )}
          </div>
        </section>

        <footer className="ml-login__footer">
          <span className="ml-login__product">data portal</span>
          <span className="ml-login__version">v{appVersion}</span>
        </footer>
      </div>
    </Theme>
  );
}
